export type Point = [number, number];

/**
 * Cubic Hermite interpolating spline through `points` — passes exactly through
 * every control point. Segment tangents are the Catmull-Rom estimates
 * m_i = (P_{i+1} − P_{i−1}) / 2, with endpoints reflected. Returns a dense
 * polyline.
 */
export function hermitePoints(points: Point[], samplesPerSegment = 24): Point[] {
    const n = points.length;
    if (n < 2) return points.slice();

    const reflectStart: Point = [2 * points[0][0] - points[1][0], 2 * points[0][1] - points[1][1]];
    const reflectEnd: Point = [
        2 * points[n - 1][0] - points[n - 2][0],
        2 * points[n - 1][1] - points[n - 2][1]
    ];
    const prevOf = (i: number): Point => (i - 1 >= 0 ? points[i - 1] : reflectStart);
    const nextOf = (i: number): Point => (i + 1 <= n - 1 ? points[i + 1] : reflectEnd);
    const tangent = (i: number): Point => {
        const a = prevOf(i);
        const b = nextOf(i);
        return [(b[0] - a[0]) / 2, (b[1] - a[1]) / 2];
    };

    const out: Point[] = [];
    for (let i = 0; i < n - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const m0 = tangent(i);
        const m1 = tangent(i + 1);
        for (let j = 0; j < samplesPerSegment; j++) {
            const t = j / samplesPerSegment;
            const t2 = t * t;
            const t3 = t2 * t;
            const h00 = 2 * t3 - 3 * t2 + 1;
            const h10 = t3 - 2 * t2 + t;
            const h01 = -2 * t3 + 3 * t2;
            const h11 = t3 - t2;
            out.push([
                h00 * p0[0] + h10 * m0[0] + h01 * p1[0] + h11 * m1[0],
                h00 * p0[1] + h10 * m0[1] + h01 * p1[1] + h11 * m1[1]
            ]);
        }
    }
    out.push(points[n - 1]);
    return out;
}

/** Build an SVG path string from a polyline in [0,1] fractional coords. */
export function toPath(poly: Point[]): string {
    if (poly.length === 0) return '';
    return poly
        .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(5)},${p[1].toFixed(5)}`)
        .join(' ');
}
